import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthenticationService, OnboardingService, LicenseService } from '@core/services';

export const authGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const authenticationService = inject(AuthenticationService);
  const onboardingService = inject(OnboardingService);
  const licenseService = inject(LicenseService);

  // ── 1. Authentication check ────────────────────────────────────────────
  const isValidUser: boolean = await authenticationService.ensureValidSession();
  if (!isValidUser) {
    authenticationService.clearUserDetail();
    router.navigate(['/login']);
    return false;
  }

  // ── 2. Onboarding check ────────────────────────────────────────────────
  const isOnboardingRoute = state.url.startsWith('/onboarding');
  const isComplete = await onboardingService.isOnboardingComplete();

  if (!isComplete && !isOnboardingRoute) {
    router.navigate(['/onboarding']);
    return false;
  }

  if (isComplete && isOnboardingRoute) {
    const urlTree = router.parseUrl(state.url);
    if (urlTree.queryParams['showComplete'] !== 'true') {
      router.navigate(['/dashboard']);
      return false;
    }
  }

  // ── 3. License / plan selection check (only after onboarding is done) ──
  // Skip for onboarding route itself
  if (isComplete) {
    // Load access context (no-op if already cached)
    await licenseService.loadAccessContext();
    const hasLicense = !licenseService.hasNoLicense;

    const isChoosePlanRoute = state.url.startsWith('/choose-plan');
    const isAcceptInviteRoute = state.url.startsWith('/accept-invite');

    if (!hasLicense) {
      if (!isChoosePlanRoute && !isAcceptInviteRoute) {
        // Check for pending invite before routing
        const inviteInfo = await licenseService.checkPendingInvite();
        if (inviteInfo.has_invite) {
          router.navigate(['/accept-invite']);
        } else {
          router.navigate(['/choose-plan']);
        }
        return false;
      }

      // Ensure they are on the right page for their state
      if (isChoosePlanRoute) {
        const inviteInfo = await licenseService.checkPendingInvite();
        if (inviteInfo.has_invite) {
          router.navigate(['/accept-invite']);
          return false;
        }
      }

      if (isAcceptInviteRoute) {
        const inviteInfo = await licenseService.checkPendingInvite();
        if (!inviteInfo.has_invite) {
          router.navigate(['/choose-plan']);
          return false;
        }
      }
    } else {
      // User already has a license but navigated to choose-plan or accept-invite
      if (isChoosePlanRoute) {
        router.navigate(['/dashboard']);
        return false;
      }
      
      if (isAcceptInviteRoute) {
        const inviteInfo = await licenseService.checkPendingInvite();
        if (!inviteInfo.has_invite) {
          router.navigate(['/dashboard']);
          return false;
        }
      }
    }
  }

  return true;
};
